export type BestXTradePrefill = {
  currencyPair: string;
  executionTimestamp: string;
  executionSpotRate: number | null;
  executionForwardPoints: number | null;
  expirationTimestamp: string;
};

type ContextRecord = Record<string, unknown>;

const LEGACY_TRADE_CONTEXT_TYPE = "bestx.selectedTrade";
const FDC3_INSTRUMENT_LIST_CONTEXT_TYPE = "fdc3.instrumentList";

const asRecord = (value: unknown): ContextRecord | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as ContextRecord)
    : null;

const getFirstString = (records: ContextRecord[], keys: string[]) => {
  for (const record of records) {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }

  return "";
};

const parseTradeNumber = (value: unknown) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getFirstTradeNumber = (records: ContextRecord[], keys: string[]) => {
  for (const record of records) {
    for (const key of keys) {
      const parsed = parseTradeNumber(record[key]);
      if (parsed != null) return parsed;
    }
  }

  return null;
};

const getFdc3InstrumentRecord = (context: ContextRecord) => {
  const instruments = context.instruments;
  if (!Array.isArray(instruments)) return null;

  const firstInstrument = asRecord(instruments[0]);
  return asRecord(firstInstrument?.id);
};

const getTradeRecords = (context: ContextRecord): ContextRecord[] => {
  if (context.type === FDC3_INSTRUMENT_LIST_CONTEXT_TYPE) {
    const instrument = getFdc3InstrumentRecord(context);
    return instrument ? [instrument] : [];
  }

  if (context.type !== LEGACY_TRADE_CONTEXT_TYPE) return [];

  const nestedId = asRecord(context.id);
  const nestedInstrument = asRecord(context.instrument);
  const nestedInstrumentId = asRecord(nestedInstrument?.id);

  return [context, nestedId, nestedInstrumentId].filter(
    (record): record is ContextRecord => record != null
  );
};

/**
 * Normalizes the supported custom and FDC3 trade contexts. Payloads without
 * the two fields required to identify a completed trade are intentionally
 * ignored, so they cannot clear an already active trade.
 */
export const parseBestXTradeContext = (
  rawContext: unknown
): BestXTradePrefill | null => {
  const context = asRecord(rawContext);
  if (!context) return null;

  const records = getTradeRecords(context);
  if (records.length === 0) return null;

  const currencyPair = getFirstString(records, [
    "ticker",
    "ccyPair",
    "currencyPair",
    "currency_pair",
    "symbol",
  ]);
  const executionTimestamp = getFirstString(records, [
    "completionTime",
    "completion_time",
    "executionTime",
    "executionTimestamp",
    "execution_timestamp",
  ]);

  if (!currencyPair || !executionTimestamp) return null;

  return {
    currencyPair,
    executionTimestamp,
    executionSpotRate: getFirstTradeNumber(records, [
      "executedRate",
      "executionRate",
      "executionSpotRate",
      "spotRate",
      "rate",
    ]),
    executionForwardPoints: getFirstTradeNumber(records, [
      "executionForwardPoints",
      "forwardPoints",
    ]),
    expirationTimestamp: getFirstString(records, [
      "valueDate",
      "value_date",
      "expiryDate",
      "expirationDate",
      "expirationTimestamp",
      "expiration_timestamp",
    ]),
  };
};
